import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const linkClass = (path: string) =>
    `relative text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${
      isActive(path)
        ? 'text-primary-500'
        : scrolled
        ? 'text-foreground-700 hover:text-primary-500'
        : 'text-white/90 hover:text-white'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-[1280px] mx-auto">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span
              className={`font-heading text-2xl transition-colors duration-200 ${
                scrolled ? 'text-primary-500' : 'text-white'
              }`}
            >
              91美
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={linkClass('/')}>
              {t('nav.home')}
            </Link>
            <Link to="/categories" className={linkClass('/categories')}>
              {t('nav.categories')}
            </Link>
            <Link to="/articles" className={linkClass('/articles')}>
              {t('nav.articles')}
            </Link>
            <Link to="/about" className={linkClass('/about')}>
              {t('nav.about')}
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer transition-colors ${
              scrolled ? 'text-foreground-800' : 'text-white'
            }`}
            aria-label={mobileOpen ? '关闭菜单' : '打开菜单'}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="flex flex-col px-4 py-3 gap-3">
            <Link to="/" className="text-foreground-800 text-sm font-medium whitespace-nowrap cursor-pointer hover:text-primary-500 transition-colors">
              {t('nav.home')}
            </Link>
            <Link to="/categories" className="text-foreground-800 text-sm font-medium whitespace-nowrap cursor-pointer hover:text-primary-500 transition-colors">
              {t('nav.categories')}
            </Link>
            <Link to="/articles" className="text-foreground-800 text-sm font-medium whitespace-nowrap cursor-pointer hover:text-primary-500 transition-colors">
              {t('nav.articles')}
            </Link>
            <Link to="/about" className="text-foreground-800 text-sm font-medium whitespace-nowrap cursor-pointer hover:text-primary-500 transition-colors">
              {t('nav.about')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}